import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  RequestItem,
  useAdministrationRequestApproveAPI,
  useProfileAPI,
  useRequestsAPI,
  useResizeObserver,
  useValidationSchemaRequest,
} from '@innowise-group/core';
import { Loader, Modal, modalHoc, ModalProps, notificationsManager, useModal, useTheme } from '@innowise-group/mui-kit';
import { RequestNonBillableWarningModal } from '@modules';
import { RequestForm } from '@shared-components';

import { requestFormDefaultValues } from './request-creation-form-modal.default-values';
import * as Styled from './request-creation-form-modal.styles';

interface RequestCreationFormModalProps {
  successCreationCallback?: (requestId: number) => void;
  // the values of a copied request (US-10.2 AC-6); otherwise the form starts empty with the user as a hiring manager
  initialValues?: RequestItem;
}

const RequestCreationFormModal: React.FC<ModalProps & RequestCreationFormModalProps> = ({
  close,
  successCreationCallback,
  initialValues,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktopView = useResizeObserver(theme.breakpoints.values.sm);
  const { profile } = useProfileAPI();
  const { validationSchema } = useValidationSchemaRequest();

  const { createRequest, isLoading: requestsIsLoading } = useRequestsAPI();
  const { isLoading: requestApproversIsLoading } = useAdministrationRequestApproveAPI();

  const methods = useForm<RequestItem>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: requestFormDefaultValues,
  });
  const { handleSubmit, reset, watch } = methods;
  const isBillable = watch('isBillable');

  const [showRequestNonBillableWarningModal] = useModal(RequestNonBillableWarningModal);

  const selectedCustomersOptions = useMemo(
    () => (initialValues ? initialValues.customers.map((item) => item.value) : [profile.id.toString()]),
    [profile, initialValues],
  );

  const onSubmit = (data: RequestItem) => {
    createRequest(data, successCallback);

    if (isBillable === 'false') {
      showRequestNonBillableWarningModal();
    }
  };

  const successCallback = (requestId: number) => {
    notificationsManager.success({ subtitle: t('notifications.text.successRequestCreation') });
    successCreationCallback?.(requestId);
    close();
  };

  // TODO(ITSRT2-1395): GET /claim/process (the non-billable approvers) is removed on the backend and comes back under
  //  a new path, maybe in a new format. The call is off until then not to fail with 404
  // useEffect(() => {
  //   getAdministrationRequestApprove();
  // }, [getAdministrationRequestApprove]);

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
      return;
    }
    if (profile) {
      reset((prev) => ({
        ...prev,
        customers: [{ value: profile.id.toString(), title: profile.localizedFullName }],
      }));
    }
  }, []);

  return (
    <Modal.Container size="large">
      {requestsIsLoading || requestApproversIsLoading ? (
        <Loader />
      ) : (
        <FormProvider {...methods}>
          <Modal.Header onClose={close}>
            <Styled.Header>
              <span>{t('pages.requests.creationModal.requestCreation')}</span>
            </Styled.Header>
          </Modal.Header>
          <Styled.Body>
            <RequestForm
              onSubmit={onSubmit}
              handleClose={close}
              selectedCustomersOptions={selectedCustomersOptions}
              isNonBillableWarningToBeShown={isBillable === 'false'}
            />
          </Styled.Body>
          {isDesktopView && (
            <Modal.Actions
              noVerticalPadding={true}
              position={'full'}
              actions={[
                {
                  title: t('buttons.cancel'),
                  onClick: close,
                  type: 'button',
                },
                {
                  title: t('buttons.save'),
                  onClick: handleSubmit(onSubmit),
                  highlighted: true,
                  type: 'button',
                },
              ]}
            />
          )}
        </FormProvider>
      )}
    </Modal.Container>
  );
};

export default modalHoc(RequestCreationFormModal, 'request-creation-form');
